"use client"

import { useState, useEffect } from "react"
import { Input } from "@/components/ui/input"
import { Patient } from "@/types"
import { Loader2 } from "lucide-react"
import { supabase } from "@/utils/supabase/client"

interface PatientSearchProps {
  onPatientSelect: (patient: Patient) => void
}

export function PatientSearch({ onPatientSelect }: PatientSearchProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [results, setResults] = useState<Patient[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (searchQuery.includes('(MR') && searchQuery.includes(')')) {
      setResults([])
      setShowResults(false)
      setError(null)
      return
    }

    if (searchQuery.length < 2) {
      setResults([])
      setShowResults(false)
      setError(null)
      return
    }

    const searchPatients = async () => {
      setIsLoading(true)
      setError(null)
      
      try {
        const { data, error } = await supabase
          .from('patients')
          .select('*')
          .or(`name.ilike.%${searchQuery}%,mr_number.ilike.%${searchQuery}%,phone.ilike.%${searchQuery}%`)
          .limit(10)
        
        if (error) {
          throw error
        }
        
        setResults(data || [])
        setShowResults(true)
        
      } catch (error: any) {
        console.error('Search error:', error)
        setError(error.message)
        setResults([])
        setShowResults(false)
      } finally {
        setIsLoading(false)
      }
    }

    const timeoutId = setTimeout(searchPatients, 300)
    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  const handleSelectPatient = (patient: Patient) => {
    setSearchQuery(`${patient.name} (MR#: ${patient.mr_number})`)
    onPatientSelect(patient)
    setShowResults(false)
  }

  const handleInputFocus = () => {
    if (results.length > 0) {
      setShowResults(true)
    }
  }

  const handleInputBlur = () => {
    setTimeout(() => setShowResults(false), 150)
  }

  return (
    <div className="relative w-full">
      <div className="relative">
        <Input
          placeholder="Search patient by name, MR number, or phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          className="w-full pr-10"
        />
        
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          </div>
        )}
      </div>

      {showResults && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
          {isLoading && (
            <div className="p-3 text-center text-gray-500">Searching...</div>
          )}

          {error && (
            <div className="p-3 text-red-600 bg-red-50">Error: {error}</div>
          )}

          {!isLoading && !error && results.length === 0 && searchQuery.length >= 2 && (
            <div className="p-3 text-center text-gray-500">
              No patients found for "{searchQuery}"
            </div>
          )}

          {!isLoading && !error && results.length > 0 && (
              results.map((patient) => (
                <div
                  key={patient.id}
                  onMouseDown={() => handleSelectPatient(patient)}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                >
                  <div className="font-medium text-gray-900">{patient.name}</div>
                  <div className="text-sm text-gray-500">
                    MR: {patient.mr_number} | Phone: {patient.phone}
                  </div>
                </div>
              ))
          )}
        </div>
      )}
    </div>
  )
}